import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { comicCategories } from "@/mocks/categories";

const NAV_LINKS = [
  { label: "首页", to: "/" },
  { label: "资讯", to: "/news" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";
  const solid = !isHome || scrolled || mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-leaf-fill text-background-50 text-xl"></i>
            </span>
            <span
              className={`font-brand text-2xl leading-none tracking-wide ${
                solid ? "text-primary-700" : "text-background-50"
              }`}
            >
              蛙漫
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.slice(0, 1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-md text-sm font-label font-bold whitespace-nowrap transition-colors ${
                  solid
                    ? "text-foreground-800 hover:text-primary-600 hover:bg-primary-50"
                    : "text-background-50 hover:bg-white/15"
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setCategoryOpen((v) => !v)}
                className={`px-3 py-2 rounded-md text-sm font-label font-bold whitespace-nowrap flex items-center gap-1 transition-colors ${
                  solid
                    ? "text-foreground-800 hover:text-primary-600 hover:bg-primary-50"
                    : "text-background-50 hover:bg-white/15"
                }`}
              >
                漫画
                <i
                  className={`ri-arrow-down-s-line transition-transform ${
                    categoryOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full mt-2 w-72 p-3 rounded-lg bg-background-50 border border-background-200 animate-fade-in">
                  <div className="grid grid-cols-2 gap-1">
                    {comicCategories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-label text-foreground-800 hover:bg-primary-50 hover:text-primary-700"
                      >
                        <i className={`${cat.icon} text-primary-500`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {NAV_LINKS.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-3 py-2 rounded-md text-sm font-label font-bold whitespace-nowrap transition-colors ${
                  solid
                    ? "text-foreground-800 hover:text-primary-600 hover:bg-primary-50"
                    : "text-background-50 hover:bg-white/15"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className="hidden md:flex items-center gap-2 h-10 px-3 rounded-full border border-background-200 bg-background-100 focus-within:border-primary-400 transition-colors"
            >
              <i className="ri-search-line text-foreground-500 text-base"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画 / 资讯"
                className="w-40 lg:w-52 bg-transparent text-sm font-label text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
              <button
                type="submit"
                className="text-xs font-label font-bold text-primary-600 hover:text-primary-700 whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="打开菜单"
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-md text-2xl transition-colors ${
                solid
                  ? "text-foreground-900 hover:bg-background-100"
                  : "text-background-50 hover:bg-white/15"
              }`}
            >
              <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-4">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 h-11 px-3 rounded-full border border-background-200 bg-background-100"
            >
              <i className="ri-search-line text-foreground-500 text-base"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索漫画 / 资讯"
                className="flex-1 bg-transparent text-sm font-label text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
              <button
                type="submit"
                className="text-xs font-label font-bold text-primary-600 whitespace-nowrap cursor-pointer"
              >
                搜索
              </button>
            </form>

            <div className="flex flex-col">
              <Link
                to="/"
                className="px-2 py-3 text-sm font-label font-bold text-foreground-900 border-b border-background-100"
              >
                首页
              </Link>
              <Link
                to="/news"
                className="px-2 py-3 text-sm font-label font-bold text-foreground-900 border-b border-background-100"
              >
                资讯
              </Link>
              <Link
                to="/about"
                className="px-2 py-3 text-sm font-label font-bold text-foreground-900 border-b border-background-100"
              >
                关于我们
              </Link>
            </div>

            <div>
              <p className="text-xs font-label font-bold text-foreground-500 mb-2">
                漫画分类
              </p>
              <div className="grid grid-cols-3 gap-2">
                {comicCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className="flex flex-col items-center gap-1 py-3 rounded-lg bg-background-100 text-xs font-label text-foreground-800"
                  >
                    <i className={`${cat.icon} text-primary-500 text-lg`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}